import React from 'react';
import { Helmet } from 'react-helmet-async';
import Hero from '../../components/Hero';
import CallToAction from '../../components/CallToAction';
import './ProjectPages.css';

// Import images
import edtechHeroImage from '../../assets/images/edtech.jpg';

const EducationTech = () => {
  const faqData = [
    {
      question: "ProgenX Akademi nedir ve nasıl çalışır?",
      answer: "ProgenX Akademi, yapay zeka destekli kişiselleştirilmiş öğrenme platformudur. Her öğrencinin öğrenme hızını, stilini ve ihtiyaçlarını analiz ederek ona özel eğitim programları sunar. Böylece her öğrenci kendi potansiyelini keşfederek başarıya ulaşır."
    },
    {
      question: "Yapay zeka destekli eğitim platformu ne demek?",
      answer: "Yapay zeka teknolojisi kullanarak öğrencilerin güçlü ve zayıf yönlerini tespit ediyoruz. Her öğrenciye özel öğrenme yolları sunuyor, anında geri bildirim veriyor ve benzer sorular önererek öğrenmeyi pekiştiriyoruz."
    },
    {
      question: "Öğrenciler platformu nasıl kullanır?",
      answer: "Öğrenciler platforma giriş yaparak kendilerine özel hazırlanmış içeriklere erişirler. Soruları çözerken anında geri bildirim alırlar, ilerlemelerini takip ederler ve eğlenceli interaktif içeriklerle öğrenirler."
    },
    {
      question: "Veliler ve öğretmenler öğrenci ilerlemesini görebilir mi?",
      answer: "Evet, veliler ve öğretmenler için detaylı raporlar hazırlanır. Hangi konularda ilerleme kaydedildiği, hangi alanlarda daha fazla çalışılması gerektiği net bir şekilde görülür."
    },
    {
      question: "Eğitim teknolojileri platformu ücretli mi?",
      answer: "Platformumuzun farklı paket seçenekleri bulunmaktadır. Detaylı bilgi ve özel teklifler için bizimle iletişime geçebilirsiniz."
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "ProgenX Akademi",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web, iOS, Android",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "TRY"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "150"
    },
    "description": "Yapay zeka destekli kişiselleştirilmiş öğrenme platformu. Her öğrenciye özel eğitim programları sunar."
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="project-page education-tech">
      <Helmet>
        <title>Eğitim Teknolojileri | Yapay Zeka Destekli Öğrenme Platformu - ProgenX Akademi 2026</title>
        <meta name="description" content="ProgenX Akademi yapay zeka destekli kişiselleştirilmiş öğrenme platformu. Her öğrenciye özel eğitim programları, akıllı soru çözümleri, gerçek zamanlı geri bildirimler. Türkiye'nin en gelişmiş eğitim teknolojisi çözümü." />
        <meta name="keywords" content="yapay zeka eğitim, kişiselleştirilmiş öğrenme, akıllı soru çözümü, online eğitim platformu, adaptif öğrenme, eğitim teknolojileri, progenx akademi, ai destekli eğitim, öğrenci başarı takibi, eğitim yazılımı" />
        <meta property="og:title" content="Eğitim Teknolojileri | Yapay Zeka Destekli Öğrenme - ProgenX" />
        <meta property="og:description" content="Her öğrenciye özel eğitim programları sunan yapay zeka destekli öğrenme platformu. Kişiselleştirilmiş öğrenme deneyimi ile başarıyı artırın." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://progenx.com.tr/projeler/egitim-teknolojileri" />
        <meta property="og:image" content="https://progenx.com.tr/edtech.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://progenx.com.tr/projeler/egitim-teknolojileri" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      <Hero 
        title="Eğitim Teknolojileri" 
        backgroundImage={edtechHeroImage}
      />

      <div className="project-content">
        <div className="container">
          <section className="project-overview">
            <h2>Her Öğrenci İçin Kişiselleştirilmiş Eğitim</h2>
            <p>
              ProgenX Akademi, her öğrencinin farklı olduğunu bilir ve bu farklılıkları güçlü yönlere dönüştürür. 
              Yapay zeka destekli platformumuz, öğrencilerin öğrenme hızını, stilini ve ihtiyaçlarını analiz ederek 
              onlara özel eğitim programları sunar. Böylece her öğrenci kendi potansiyelini keşfederek başarıya ulaşır.
            </p>
          </section>

          <section className="features">
            <h2>Öğrencilere Nasıl Yardımcı Oluyoruz?</h2>
            <div className="features-grid">
              <div className="feature-item">
                <h3>Kişiselleştirilmiş Öğrenme Deneyimi</h3>
                <p>
                  Her öğrencinin öğrenme hızı ve stili farklıdır. Platformumuz, öğrencilerin 
                  güçlü ve zayıf yönlerini tespit ederek onlara özel öğrenme yolları sunar. 
                  Böylece her öğrenci kendi hızında ilerleyerek daha etkili öğrenir.
                </p>
              </div>

              <div className="feature-item">
                <h3>Anında Geri Bildirim ve Rehberlik</h3>
                <p>
                  Öğrenciler soruları çözerken anında geri bildirim alırlar. Hatalarını neden 
                  yaptıklarını öğrenir, doğru çözüm yollarını keşfederler. Sistem, benzer sorular 
                  önererek öğrenmeyi pekiştirir ve başarıyı artırır.
                </p>
              </div>

              <div className="feature-item">
                <h3>Detaylı İlerleme Takibi</h3>
                <p>
                  Öğrenciler, veliler ve öğretmenler için hazırlanan detaylı raporlar sayesinde 
                  hangi konularda ilerleme kaydedildiği, hangi alanlarda daha fazla çalışılması 
                  gerektiği net bir şekilde görülür.
                </p>
              </div>

              <div className="feature-item">
                <h3>Eğlenceli ve Etkileşimli İçerikler</h3>
                <p>
                  Öğrenmeyi eğlenceli hale getiren interaktif içerikler, oyunlaştırma elementleri 
                  ve grup çalışmaları ile öğrencilerin motivasyonu artar ve öğrenme süreci 
                  daha keyifli hale gelir.
                </p>
              </div>
            </div>
          </section>

          <section className="benefits">
            <h2>Öğrenciler İçin Ne Sağlıyoruz?</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <h3>Kendi Hızında Öğrenme</h3>
                <p>Her öğrenci kendi öğrenme hızında ilerler. Kimse geride kalmaz, kimse sıkılmaz. 
                Herkes kendi potansiyelini keşfeder ve en iyi versiyonuna ulaşır.</p>
              </div>

              <div className="benefit-item">
                <h3>Daha Hızlı ve Etkili Öğrenme</h3>
                <p>Kişiselleştirilmiş içerikler ve akıllı öneriler sayesinde öğrenciler 
                daha kısa sürede daha fazla şey öğrenir ve öğrendiklerini daha iyi hatırlar.</p>
              </div>

              <div className="benefit-item">
                <h3>Sürekli Gelişim ve İlerleme</h3>
                <p>Düzenli geri bildirimler ve önerilerle öğrenciler sürekli gelişir. 
                Hangi konuda ne kadar ilerlediklerini görür, hedeflerine daha kolay ulaşırlar.</p>
              </div>

              <div className="benefit-item">
                <h3>Öğrenmeyi Sevdiren Deneyim</h3>
                <p>Eğlenceli içerikler, başarı ödülleri ve interaktif öğrenme yöntemleri ile 
                öğrencilerin motivasyonu artar ve öğrenmeyi severler.</p>
              </div>
            </div>
          </section>

          <section className="faq-section">
            <h2>Sıkça Sorulan Sorular</h2>
            <div className="faq-container">
              {faqData.map((faq, index) => (
                <div key={index} className="faq-item">
                  <h3 className="faq-question">{faq.question}</h3>
                  <p className="faq-answer">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <CallToAction 
        title="Eğitimde Yeni Bir Dönem Başlıyor"
        subtitle="Her öğrencinin potansiyelini keşfettiği, kişiselleştirilmiş eğitim deneyimi için bizimle iletişime geçin."
        buttonText="Hemen Başlayın"
        buttonLink="/iletisim"
      />
    </div>
  );
};

export default EducationTech; 